import { PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';

import { AddIcon, ChevronRightIcon } from '@treams/treams-icons';
import { IconButton, LabelPlus, tokens } from '@treams/treams-ui';

const SectionBlock = styled.div`
	width: 100%;
	border-bottom: 1px solid ${tokens.border.subtle};
`;

const ROW_GLYPH = 20;

const Row = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	box-sizing: border-box;
	height: ${tokens.padding.container * 2 + ROW_GLYPH}px;
	padding: 0;
`;

const Toggle = styled.button`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.inline}px;
	flex: 1;
	min-width: 0;
	margin: 0;
	padding: 0;
	border: 0;
	background: transparent;
	cursor: pointer;
	text-align: left;
	font: inherit;
	color: inherit;
`;

const Chevron = styled.span<{ $open?: boolean }>`
	display: flex;
	flex-shrink: 0;
	width: 16px;
	height: 16px;
	color: ${tokens.icon.secondary};
	transform: rotate(${(props) => (props.$open ? '90deg' : '0deg')});
	transition: transform 200ms ease-out;

	svg {
		display: block;
	}
`;

const Glyph = styled.span`
	display: flex;
	flex-shrink: 0;
	width: ${ROW_GLYPH}px;
	height: ${ROW_GLYPH}px;
	color: ${tokens.icon.secondary};

	svg {
		display: block;
	}
`;

const SectionLabel = styled(LabelPlus)`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Peek = styled.div`
	padding-bottom: ${tokens.padding.container}px;
`;

export function Section(
	props: PropsWithChildren<{
		title: string;
		icon: ReactNode;
		isOpen: boolean;
		onToggle: () => void;
		createLink?: string;
	}>,
) {
	return (
		<SectionBlock>
			<Row>
				<Toggle
					type="button"
					onClick={props.onToggle}
					aria-expanded={props.isOpen}
					aria-label={props.title}
				>
					<Chevron $open={props.isOpen}>
						<ChevronRightIcon size={16} />
					</Chevron>
					<Glyph>{props.icon}</Glyph>
					<SectionLabel>{props.title}</SectionLabel>
				</Toggle>
				{props.createLink && (
					<IconButton
						type="plain"
						icon={<AddIcon />}
						link={props.createLink}
						onClick={(event) => event.stopPropagation()}
					/>
				)}
			</Row>
			{props.isOpen && <Peek>{props.children}</Peek>}
		</SectionBlock>
	);
}
